import Vue from "vue";
import { MAP_DEFAULT_POSITION, MAP_STYLE_DEFAULT } from "@/config/const";
import { firebase, db } from "@/firebase/firebaseConfig";
import AuthService from "@/services/authService";
import LocalDbService from "@/services/localDbService";
import { VesselMixin, PlotMixin } from "@/mixins";
import isString from "lodash/isString";
import { isObject } from "lodash";

const COLLECTIONS = {
  ENTERPRISE_ACCOUNT_SETTINGS: "enterprise_account_settings",
  ENTERPRISE_ACCOUNTS: "enterprise_accounts",
  WEATHER: "weather",
  ENGINE_DATA: "engine_data",
};

class FireStoreService {
  /**
   * ユーザーの設定をFireStoreから取得しindexDBに設定する。
   * FireStoreにない場合はデフォルト値があるものだけをindexDBに設定する。
   */
  async setEnterpriseAccountSettingsToLocalDb() {
    let user = await AuthService.getUser();
    let settings =
      (await this.get(
        COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
        user.account_id
      )) || {};
    if (settings) {
      // 初期表示位置
      if (settings.default_position) {
        let dp = settings.default_position;
        let defaultPosition = {
          center: [dp.center.longitude, dp.center.latitude],
          zoom: dp.zoom,
        };
        await LocalDbService.saveDefaultPosition(defaultPosition);
      } else {
        await LocalDbService.saveDefaultPosition(MAP_DEFAULT_POSITION);
      }
      // マップのスタイル
      if (settings.map_style) {
        await LocalDbService.saveMapStyle(settings.map_style);
      } else {
        await LocalDbService.saveMapStyle(MAP_STYLE_DEFAULT);
      }
      // 選択船
      if (settings.selected_vessels) {
        await LocalDbService.saveSelectedVessels(settings.selected_vessels);
      }
      // 船のフィルター設定
      if (settings.filter_vessels) {
        await LocalDbService.saveFilterVessels(settings.filter_vessels);
      } else {
        let vesselCategoryIds = VesselMixin.methods.getVesselCategoryIds();
        await LocalDbService.saveFilterVessels(vesselCategoryIds);
      }
      // プロットのフィルター設定
      if (settings.filter_plots) {
        await LocalDbService.saveFilterPlots(settings.filter_plots);
      } else {
        let plotCategoryIds = PlotMixin.methods.getPlotParentCategoryIds();
        await LocalDbService.saveFilterPlots(plotCategoryIds);
      }
      // 航路、標識のフィルター設定
      if (settings.filter_navigations) {
        await LocalDbService.saveFilterNavigations(settings.filter_navigations);
      }

      // 航路、標識のフィルター設定
      if (settings.filter_radar_grid) {
        await LocalDbService.saveFilterRadarGrid(settings.filter_radar_grid);
      }

      // 自船の表示時時間
      if (settings.own_vessel_display_duration_time) {
        await LocalDbService.saveOwnVesselDisplayDurationTime(
          settings.own_vessel_display_duration_time
        );
      } else {
        await LocalDbService.saveOwnVesselDisplayDurationTime(0);
      }
    }
  }

  async getEnterpriseAccountSettings() {
    let user = await AuthService.getUser();
    let settings =
      (await this.get(
        COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
        user.account_id
      )) || {};
    return settings;
  }

  /**
   * 初期表示位置をFireStoreに保存する
   * @param map 緯度軽度とZoom値
   */
  async saveDefaultPosition(map) {
    if (map === undefined) return;
    let user = await AuthService.getUser();
    let geoPoint = new firebase.firestore.GeoPoint(
      map.center[1], // 緯度
      map.center[0] // 経度
    );
    let data = {
      default_position: { center: geoPoint, zoom: map.zoom },
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true
    );
  }

  /**
   * MapのStyleをFireStoreに保存する
   * @param style MapのStyle
   */
  async saveMapStyle(style) {
    if (style === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      map_style: style,
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true
    );
  }

  /**
   * 選択中の船舶をFireStoreに保存する
   * @param vesselIds 選択中の船舶ID一覧
   */
  async saveSelectedVessels(vesselIds) {
    if (vesselIds === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      selected_vessels: vesselIds,
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true
    );
  }

  /**
   * 船舶のフィルター対象カテゴリーIDDをFireStoreに保存する
   * @param targetCategoryIds 船舶のフィルター対象のカテゴリーID一覧
   */
  async saveFilterVessels(targetCategoryIds) {
    if (targetCategoryIds === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      filter_vessels: targetCategoryIds,
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true
    );
  }

  /**
   * プロットのフィルター対象カテゴリIDをFireStoreに保存する
   * @param targetCategoryIds プロットのフィルター対象のカテゴリーID（親）一覧
   */
  async saveFilterPlots(targetCategoryIds) {
    if (targetCategoryIds === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      filter_plots: targetCategoryIds,
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true
    );
  }

  /**
   * 航路、標識ののフィルター対象カテゴリIDをFireStoreに保存する
   * @param targetCategoryIds 航路、標識のフィルター対象のカテゴリーID一覧
   */
  async saveFilterNavigations(targetCategoryIds) {
    if (targetCategoryIds === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      filter_navigations: targetCategoryIds,
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true
    );
  }

  /**
   * RGののフィルター対象カテゴリIDをFireStoreに保存する
   * @param targetCategoryIds ID一覧
   */
  async saveFilterRadarGrid(targetCategoryIds) {
    if (targetCategoryIds === undefined) return;
    const user = await AuthService.getUser();
    const data = {
      filter_radar_grid: targetCategoryIds,
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true
    );
  }

  /**
   * 自船の表示時間をFireStoreに保存する
   * @param durationTime 自船の表示時間(分)
   */
  async saveOwnVesselDisplayDurationTime(durationTime) {
    if (durationTime === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      own_vessel_display_duration_time: durationTime,
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true
    );
  }

  /**
   * 沖波ポイントをFireStoreに保存する
   * @param points 沖波ポイント一覧
   */
  async saveOffingWavePoints(points) {
    if (points === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      polaris_offing_wave_points: points,
    };
    await this.set(COLLECTIONS.WEATHER, user.account_id, data, true);
  }

  /**
   * fireStoreのアカウントと船舶に紐づく機関情報設定を取得する.
   * @param {string} vesselId 船舶
   * @return {Object} アカウントと船舶に紐づく機関情報設定 {key: value}
   */
  async getEngineDataSetting(vesselId) {
    if (!vesselId) return {};
    const user = await AuthService.getUser();
    const ref = db
      .collection(COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS)
      .doc(user.account_id)
      .collection(COLLECTIONS.ENGINE_DATA)
      .doc(`v:${vesselId}`);

    return ref
      .get()
      .then(doc => {
        if (!doc.exists) {
          return {}; // {}検知で空オブジェクトを返す
        } else {
          return doc.data();
        }
      })
      .catch(err => {
        Vue.$log.error("Error setting document", err);
        return {};
      });
  }
  /**
   * fireStoreのアカウントと船舶に紐づく機関情報設定を保存する.
   * @param {string} vesselId 船舶
   * //@param {object} data 保存したいデータ {key: value}
   * @param {string} settingKey 保存したい設定のkey
   * @param {string} settingValue 保存したい設定の値
   */
  async setEngineDataSetting(vesselId, settingKey, settingValue) {
    if (!vesselId) return null;
    if (!settingKey) return null;
    const user = await AuthService.getUser();

    return db
      .collection(COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS)
      .doc(user.account_id)
      .collection(COLLECTIONS.ENGINE_DATA)
      .doc(`v:${vesselId}`)
      .set({ [settingKey]: settingValue }, { merge: true })
      .catch(err => {
        Vue.$log.error("Error setting document", err);
        return null;
      });
  }

  /**
   *  fireStoreの個別アカウント設定を取得する.
   */
  async getAccountSetting(settingDocName) {
    const accountId = await this.getFirebaseAccountId();
    if (accountId) {
      const docData = await this.get(
        `accounts/${accountId}/setting/`,
        settingDocName
      );
      return docData;
    } else {
      return null;
    }
  }
  /**
   *  fireStoreの個別アカウント設定を保存する.
   */
  async setAccountSetting(settingDocName, data) {
    const accountId = await this.getFirebaseAccountId();
    if (accountId) {
      this.set(`accounts/${accountId}/setting/`, settingDocName, data, true);
    }
  }
  /**
   *  firebase認証で使うaccountIdを抽出する.
   */
  async getFirebaseAccountId() {
    const firebaseUser = firebase.auth().currentUser;
    if (firebaseUser) {
      const parsedToken = await firebaseUser.getIdTokenResult();
      return parsedToken.claims.accountId;
    } else {
      return null;
    }
  }

  /**
   *  operationの設定を取得
   */
  async getOperationSettings(settingDocName) {
    if (!isString(settingDocName)) return {};
    if (settingDocName === "") return {};

    const user = await AuthService.getUser();
    const enterpriseId = user.id;
    const accountId = await this.getFirebaseAccountId();

    if (enterpriseId && accountId) {
      const docData = await this.get(
        `${COLLECTIONS.ENTERPRISE_ACCOUNTS}/e:${enterpriseId}/a:${accountId}/settings/operation_settings/`,
        settingDocName
      );
      return docData;
    } else {
      return {};
    }
  }

  /**
   *  operationの設定をセット
   */
  async setOperationSettings(settingDocName, data) {
    if (!isString(settingDocName)) return;
    if (settingDocName === "") return;
    if (!isObject(data)) return;

    const user = await AuthService.getUser();
    const enterpriseId = user.id;
    const accountId = await this.getFirebaseAccountId();

    if (enterpriseId && accountId) {
      await this.set(
        `${COLLECTIONS.ENTERPRISE_ACCOUNTS}/e:${enterpriseId}/a:${accountId}/settings/operation_settings/`,
        settingDocName,
        data,
        true
      );
    }
  }

  /**
   * ドキュメントを取得
   */
  async get(collection, documentPath) {
    if (documentPath) {
      let ref = db.collection(collection).doc(documentPath);
      return ref
        .get()
        .then(doc => {
          if (!doc.exists) {
            return null;
          } else {
            return doc.data();
          }
        })
        .catch(err => {
          Vue.$log.error("Error getting document", err);
          return null;
        });
    } else {
      return null;
    }
  }

  /**
   * ドキュメントを作成または上書き
   */
  async set(collection, documentPath, data, isMerge) {
    if (documentPath) {
      return db
        .collection(collection)
        .doc(documentPath)
        .set(data, { merge: isMerge })
        .catch(err => {
          Vue.$log.error("Error setting document", err);
          return null;
        });
    } else {
      return null;
    }
  }

  async cleanupLocalData() {
    try {
      await firebase.auth().signOut(); // ローカルの firebase のデータ削除
      return true;
    } catch {
      return false;
    }
  }
}

export default new FireStoreService();
